import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Star, Clock, Eye, Film, MessageSquare, Lightbulb, ThumbsUp, ThumbsDown, MapPin, Briefcase, DollarSign } from "lucide-react";
import { FeedbackResultDoc } from "@/dao/firestore/FeedbackResultsRepository";
import { PersonaDoc } from "@/dao/firestore/PersonasRepository";
import { useTranslation } from "react-i18next";
import { marked } from "marked";
interface PersonalFeedbackModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  result: FeedbackResultDoc | null;
  persona?: PersonaDoc;
}
export function PersonalFeedbackModal({
  open,
  onOpenChange,
  result,
  persona
}: PersonalFeedbackModalProps) {
  const {
    t
  } = useTranslation();
  if (!result) return null;
  const formatTime = (milliseconds: number) => {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  const formatTimeFromSeconds = (value: string | number) => {
    const seconds = Math.floor(typeof value === 'string' ? parseFloat(value) : value);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  const formatIncome = (income?: number) => {
    if (!income) return '';
    return income >= 1000 ? `${(income / 1000).toFixed(0)}k€` : `${income}€`;
  };
  const getRatingColor = (rating: number) => {
    if (rating >= 8) return "text-green-600";
    if (rating >= 6) return "text-yellow-600";
    return "text-red-600";
  };
  const parseMarkdown = (text: string) => {
    try {
      return marked.parse(text);
    } catch (error) {
      return text;
    }
  };
  const getInsightIcon = (insight: any) => {
    // Check both type and comment for sentiment indicators
    const textToAnalyze = `${insight.type || ''} ${insight.comment || ''}`.toLowerCase();

    // Positive sentiment keywords
    const positiveKeywords = ['like', 'good', 'positive', 'mejor', 'bueno', 'excelente', 'great', 'awesome', 'love', 'perfect', 'amazing', 'fantastic', 'wonderful', 'outstanding', 'works well', 'clear hook', 'playful'];

    // Negative sentiment keywords  
    const negativeKeywords = ['dislike', 'bad', 'negative', 'peor', 'malo', 'terrible', 'awful', 'hate', 'horrible', 'disgusting', 'poor', 'disappointing', 'worst', 'lost', 'confusing', 'unclear', 'boring', 'slow', 'quick', 'feels like a lost'];
    const isNegative = negativeKeywords.some(keyword => textToAnalyze.includes(keyword));
    const isPositive = positiveKeywords.some(keyword => textToAnalyze.includes(keyword));
    if (isNegative) {
      return <ThumbsDown className="h-4 w-4 text-red-500" />;
    }
    if (isPositive) {
      return <ThumbsUp className="h-4 w-4 text-green-500" />;
    }
    return null;
  };
  return <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl h-[95vh] p-0 flex flex-col">
        <DialogHeader className="p-6 pb-4 flex-shrink-0">
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0 flex flex-col items-center">
              <div className="relative">
                <Avatar className="h-48 w-48">
                  <AvatarImage src={persona?.image || '/placeholder.svg'} alt={result.personaName} />
                  <AvatarFallback className="text-4xl">{result.personaName?.charAt(0) || '?'}</AvatarFallback>
                </Avatar>
                <div className="absolute bottom-0 left-0 right-0 bg-white/80 backdrop-blur-sm rounded-b-full px-3 py-2">
                  <div className="flex items-center justify-center gap-2">
                    <Star className="h-6 w-6 text-yellow-500 fill-current" />
                    <span className={`text-3xl font-bold ${getRatingColor(result.rating || 0)}`}>
                      {result.rating || 0}/10
                    </span>
                  </div>
                </div>
              </div>
              <div className="mt-4">
                
              </div>
            </div>
            
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-xl mb-3">{result.personaName} {t('common.inTheStyleOfSuffix')}</DialogTitle>
              
              {/* Información básica */}
              <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                {persona?.age && <span>{persona.age} {t('studio.critics.years')}</span>}
                {persona?.location?.city && <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    <span>{persona.location.city}</span>
                  </div>}
                {persona?.occupation && <div className="flex items-center gap-1">
                    <Briefcase className="h-3 w-3" />
                    <span>{persona.occupation}</span>
                  </div>}
                {persona?.estimated_income_eur && <div className="flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    <span>{formatIncome(persona.estimated_income_eur)}</span>
                  </div>}
              </div>

              {/* Descripción de la persona */}
              {persona?.description && <div className="mb-4">
                  <div className="text-sm text-muted-foreground leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                __html: parseMarkdown(persona.description)
              }} />
                </div>}

              {/* Intereses */}
              {persona?.interests && persona.interests.length > 0 && <div>
                  
                  <div className="flex flex-wrap gap-2">
                    {persona.interests.map(interest => <Badge key={interest} variant="secondary" className="text-xs">
                        {interest}
                      </Badge>)}
                  </div>
                </div>}
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 h-[calc(95vh-120px)] px-6 pb-6">
          <div className="space-y-6">
            {/* Executive Summary */}
            {result.comment && <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    {t('studio.critics.executiveSummary')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                __html: parseMarkdown(result.comment)
              }} />
                </CardContent>
              </Card>}

            {/* Better and Worst Moments */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {result.better_moment && <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3 text-green-600">
                      <ThumbsUp className="h-4 w-4" />
                      <span className="font-semibold">{t('studio.critics.betterMoment')}</span>
                      <Badge variant="outline" className="text-xs">
                        {formatTime(result.better_moment.millisecond || 0)}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                  __html: parseMarkdown(result.better_moment.comment || '')
                }} />
                  </CardContent>
                </Card>}

                {result.worst_moment && <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-3 text-red-600">
                      <ThumbsDown className="h-4 w-4" />
                      <span className="font-semibold">{t('studio.critics.worstMoment')}</span>
                      <Badge variant="outline" className="text-xs">
                        {formatTime(result.worst_moment.millisecond || 0)}
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                  __html: parseMarkdown(result.worst_moment.comment || '')
                }} />
                  </CardContent>
                </Card>}
            </div>

            {/* Full Opinion */}
            {result.opinion && <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Film className="h-5 w-5" />
                    {t('studio.critics.fullOpinion')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none text-muted-foreground" dangerouslySetInnerHTML={{
                __html: parseMarkdown(result.opinion)
              }} />
                </CardContent>
              </Card>}

            {/* Detailed Evaluation by Category */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  {t('studio.critics.detailedEvaluation')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {result.visual && <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{t('studio.critics.visual')}</span>
                      <span className={`font-semibold ${getRatingColor(result.visual.rating)}`}>
                        {result.visual.rating}/10
                      </span>
                    </div>
                    <Progress value={result.visual.rating * 10} className="mb-2" />
                    <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                  __html: parseMarkdown(result.visual.comment)
                }} />
                  </div>}

                {result.content && <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{t('studio.critics.content')}</span>
                        <span className={`font-semibold ${getRatingColor(result.content.rating)}`}>
                          {result.content.rating}/10
                        </span>
                      </div>
                      <Progress value={result.content.rating * 10} className="mb-2" />
                      <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                    __html: parseMarkdown(result.content.comment)
                  }} />
                    </div>
                  </>}

                {result.script && <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{t('studio.critics.script')}</span>
                        <span className={`font-semibold ${getRatingColor(result.script.rating)}`}>
                          {result.script.rating}/10
                        </span>
                      </div>
                      <Progress value={result.script.rating * 10} className="mb-2" />
                      <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                    __html: parseMarkdown(result.script.comment)
                  }} />
                    </div>
                  </>}

                

                {result.sound_design && <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{t('studio.critics.soundDesign')}</span>
                        <span className={`font-semibold ${getRatingColor(result.sound_design.rating)}`}>
                          {result.sound_design.rating}/10
                        </span>
                      </div>
                      <Progress value={result.sound_design.rating * 10} className="mb-2" />
                      <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                    __html: parseMarkdown(result.sound_design.comment)
                  }} />
                    </div>
                  </>}

                {result.pacing && <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{t('studio.critics.pacing')}</span>
                        <span className={`font-semibold ${getRatingColor(result.pacing.rating)}`}>
                          {result.pacing.rating}/10
                        </span>
                      </div>
                      <Progress value={result.pacing.rating * 10} className="mb-2" />
                      <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                    __html: parseMarkdown(result.pacing.comment)
                  }} />
                    </div>
                  </>}

                {result.performance && <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{t('studio.critics.performance')}</span>
                        <span className={`font-semibold ${getRatingColor(result.performance.rating)}`}>
                          {result.performance.rating}/10
                        </span>
                      </div>
                      <Progress value={result.performance.rating * 10} className="mb-2" />
                      <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                    __html: parseMarkdown(result.performance.comment)
                  }} />
                    </div>
                  </>}

                {result.target_focus && <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{t('studio.critics.targetFocus')}</span>
                        <span className={`font-semibold ${getRatingColor(result.target_focus.rating)}`}>
                          {result.target_focus.rating}/10
                        </span>
                      </div>
                      <Progress value={result.target_focus.rating * 10} className="mb-2" />
                      <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                    __html: parseMarkdown(result.target_focus.comment)
                  }} />
                    </div>
                  </>}

                  {result.other && <>
                    <Separator />
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium">{t('studio.critics.other')}</span>
                        <span className={`font-semibold ${getRatingColor(result.other.rating)}`}>
                          {result.other.rating}/10
                        </span>
                      </div>
                      <Progress value={result.other.rating * 10} className="mb-2" />
                      <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                    __html: parseMarkdown(result.other.comment)
                  }} />
                    </div>
                  </>}
                  
              </CardContent>
            </Card>

            {/* Recommendations */}
            {result.recommendations && <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Lightbulb className="h-5 w-5" />
                    {t('studio.critics.recommendations.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                __html: parseMarkdown(result.recommendations)
              }} />
                </CardContent>
              </Card>}

            {/* Specific Insights */}
            {result.insights && result.insights.length > 0 && <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    {t('studio.critics.specificInsights')} ({result.insights.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {result.insights.map((insight, index) => <div key={index} className="border-l-2 border-primary/20 pl-4 py-2">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">
                              {formatTimeFromSeconds(insight.start)} - {formatTimeFromSeconds(insight.end)}
                            </Badge>
                            {getInsightIcon(insight)}
                          </div>
                        <div className="text-sm text-muted-foreground prose prose-sm max-w-none" dangerouslySetInnerHTML={{
                    __html: parseMarkdown(insight.comment)
                  }} />
                      </div>)}
                  </div>
                </CardContent>
              </Card>}

          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>;
}